import { zodResolver } from '@hookform/resolvers/zod';
import {
  QButton,
  QFormControl,
  QInput,
  QModal,
  QModalBody,
  QModalHeader,
  QStack,
  QText,
  QTextarea,
  QBox,
} from '@qualio/ui-components';
import React, { useCallback } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Signature, ZSignature } from '../../types/signature';
import { useMakeTemplateEffective } from '../../hooks/useMakeTemplateEffective';

export type MakeEffectiveModalProps = {
  templateUuid: string;
  onClose: () => void;
  onBeforeSubmit?: () => Promise<void>;
};

export const MakeEffectiveModal: React.FC<MakeEffectiveModalProps> = ({ templateUuid, onClose, onBeforeSubmit }) => {
  const { t } = useTranslation();
  const formMethods = useForm<Signature>({
    resolver: zodResolver(ZSignature),
    mode: 'onChange',
  });
  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = formMethods;
  const formHasErrors = !!Object.keys(errors).length;
  const { mutateAsync: makeEffectiveMutate, isLoading: isMakeEffectiveLoading } =
    useMakeTemplateEffective(templateUuid);

  const handleClose = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  const onSubmit = useCallback(
    async (data: Signature) => {
      if (onBeforeSubmit) await onBeforeSubmit();
      await makeEffectiveMutate(data);
      onClose();
    },
    [makeEffectiveMutate, onClose, onBeforeSubmit],
  );

  return (
    <QModal isOpen={true} onClose={handleClose} size="lg">
      <QModalHeader>
        <QText>{t('template.makeEffective.modalTitle')}</QText>
      </QModalHeader>
      <QModalBody>
        <FormProvider {...formMethods}>
          <form data-cy="form" onSubmit={handleSubmit(onSubmit)}>
            <QText mb={5}>{t('template.makeEffective.instructions')}</QText>
            <QStack spacing="18px">
              <QFormControl
                label={t('template.makeEffective.emailLabel')}
                isInvalid={!!errors.email}
                isRequired={true}
                error={errors.email?.message?.toString().trim()}
              >
                <Controller
                  control={control}
                  name="email"
                  render={(controllerProps) => (
                    <QInput
                      {...controllerProps.field}
                      data-cy="signature-email"
                      placeholder={t('template.makeEffective.emailPlaceholder')}
                    />
                  )}
                />
              </QFormControl>
              <QFormControl
                label={t('template.makeEffective.passwordLabel')}
                isInvalid={!!errors.password}
                isRequired={true}
                error={errors.password?.message?.toString().trim()}
              >
                <Controller
                  control={control}
                  name="password"
                  render={(controllerProps) => (
                    <QInput
                      {...controllerProps.field}
                      data-cy="signature-password"
                      type="password"
                      placeholder={t('template.makeEffective.passwordPlaceholder')}
                    />
                  )}
                />
              </QFormControl>
              <QFormControl
                label={t('template.makeEffective.reasonLabel')}
                isInvalid={!!errors.comment}
                isRequired={true}
                error={errors.comment?.message?.toString().trim()}
              >
                <Controller
                  control={control}
                  name="comment"
                  render={(controllerProps) => (
                    <QTextarea
                      {...controllerProps.field}
                      placeholder={t('template.makeEffective.reasonPlaceholder')}
                      data-cy="signature-reason"
                    />
                  )}
                />
              </QFormControl>
            </QStack>
            <QBox mt={5} mb={3} display="flex" flexDirection="row" justifyContent="flex-end">
              <QBox mr={3}>
                <QButton
                  variant="outline"
                  data-cy="signature-modal-cancel-button"
                  onClick={handleClose}
                  isDisabled={isMakeEffectiveLoading}
                >
                  {t('common.buttons.cancel')}
                </QButton>
              </QBox>
              <QButton
                data-cy="signature-modal-submit-button"
                onClick={handleSubmit(onSubmit)}
                isDisabled={isMakeEffectiveLoading || formHasErrors}
                isLoading={isMakeEffectiveLoading}
              >
                {t('template.makeEffective.buttonLabel')}
              </QButton>
            </QBox>
          </form>
        </FormProvider>
      </QModalBody>
    </QModal>
  );
};
